import * as React from 'react';
import {useDispatch} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import {ReportChart} from '../../../reports/report-chart';
import {ReportDataSummary} from '../../../reports/report-data-summary';
import {COMPLETED_ACTIONS} from '../../../reports/report-types';
import type {ChartReportParams, ReportURLParams} from '../../../reports/report-types';

import * as actions from './completed-actions-actions';
import {CompletedActionsReportProvider} from './completed-actions-report-provider';

type Props = {
    activeFilters: FilterObject[];
    onChartClick?: (filters: FilterObject) => void | null | undefined;
    onParamChange: (arg1: any, arg2: boolean) => void;
    reportParams: ChartReportParams;
    // This chart does not use the list view so this can be "*"
    urlParams: ReportURLParams<any>;
    currentGapIndex: number;
    segmentBy?: string | null | undefined;
};

export function CompletedActionsChart(props: Props) {
    const dispatch = useDispatch();

    const fetchReportData = React.useCallback(
        (reportParams: ChartReportParams) => {
            dispatch(
                actions.requestReportData({
                    reportParams: {
                        ...reportParams,
                        period:
                            (props.urlParams.filter && props.urlParams.filter.createdTime) ||
                            '-d30',
                    },
                    segmentBy: props.segmentBy,
                })
            );
        },
        [dispatch, props.urlParams.filter, props.segmentBy]
    );

    const activeFiltersString = JSON.stringify(props.urlParams.filter);
    const reportParamsString = JSON.stringify(props.reportParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedReportParams = React.useMemo(() => props.reportParams, [reportParamsString]);

    React.useEffect(() => {
        fetchReportData(memoizedReportParams);
    }, [fetchReportData, memoizedReportParams, activeFiltersString, props.segmentBy]);

    const reportDataSummary = (
        <ReportDataSummary
            currentGapIndex={props.currentGapIndex}
            filterParams={props.urlParams.filter}
            reportParams={memoizedReportParams}
            reportKey={COMPLETED_ACTIONS}
        />
    );

    return (
        <CompletedActionsReportProvider>
            <ReportChart
                {...props}
                reportParams={{...memoizedReportParams, facet: 'count', segment: 'actionType'}}
                reportKey={COMPLETED_ACTIONS}
                hasBorder={false}
                isFullWidth={true}
                reportDataSummary={reportDataSummary}
            />
        </CompletedActionsReportProvider>
    );
}
